import { getWizardColors } from '../catalog'
import {
  getGalleryPrintMeshes,
  getGalleryWallMeshes,
} from '../galleryMeshCatalog'
import { FRAME_NONE, type WizardDraft, type WizardPrintSlot } from '../types'

/** Runtime pattern draft shape the editor restores from notes */
export interface GalleryPatternDraft {
  templateID: number
  frameID: number | null
  matting: { width: number }
  printTiling: number
  materialID: Record<string, number>
  print: Record<string, { id: string | number; url?: string; rotation?: number }>
  color: Record<string, Array<{ r: number; g: number; b: number }>>
  merge: string[]
  hidden: Record<string, boolean>
  position: Record<string, { x: number; z: number }>
  accentID: null
  sceneID: null
}

function hexToRgb01(hex: string): { r: number; g: number; b: number } {
  const raw = hex.replace('#', '').trim()
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw
  const n = parseInt(full, 16)
  if (Number.isNaN(n)) return { r: 0.95, g: 0.95, b: 0.95 }
  return {
    r: ((n >> 16) & 255) / 255,
    g: ((n >> 8) & 255) / 255,
    b: (n & 255) / 255,
  }
}

function includedSlots(slots: WizardPrintSlot[]): WizardPrintSlot[] {
  return slots.filter((s) => s.photoId && s.egoId && !s.excluded)
}

/**
 * Build the same PatternDraft shape guest `copyDraftToTopic` writes —
 * so `/pattern?topicID=` RestorePattern applies prints/materials/color.
 */
export function buildGalleryPatternDraft(draft: WizardDraft): GalleryPatternDraft {
  const templateID = Number(draft.sceneId ?? 400)
  const printMeshes = getGalleryPrintMeshes(templateID)
  const wallMeshes = getGalleryWallMeshes(templateID)
  const photos = includedSlots(draft.slots)

  const print: GalleryPatternDraft['print'] = {}
  photos.forEach((slot, i) => {
    const mesh = printMeshes[i]
    if (!mesh || !slot.egoId) return
    print[mesh] = {
      id: `egoID-${slot.egoId}`,
      url: slot.url || slot.thumbUrl || '',
      rotation: 0,
    }
  })

  const materialID: Record<string, number> = {}
  if (draft.materialId != null) {
    const matId = Number(draft.materialId)
    if (!Number.isNaN(matId)) {
      printMeshes.forEach((mesh) => {
        materialID[mesh] = matId
      })
    }
  }

  const color: GalleryPatternDraft['color'] = {}
  if (draft.colorId) {
    const swatch = getWizardColors(24).find((c) => c.id === draft.colorId)
    if (swatch?.hex) {
      const rgb = hexToRgb01(swatch.hex)
      wallMeshes.forEach((mesh) => {
        color[mesh] = [rgb]
      })
    }
  }

  const frameNone =
    draft.frameId == null || draft.frameId === FRAME_NONE || draft.frameId === '__none__'
  const frameID = frameNone ? null : Number(draft.frameId)
  const safeFrame = frameID != null && !Number.isNaN(frameID) ? frameID : null

  return {
    templateID: Number.isNaN(templateID) ? 400 : templateID,
    frameID: safeFrame,
    matting: { width: typeof draft.matWidth === 'number' ? draft.matWidth : 0 },
    printTiling: 1,
    materialID,
    print,
    color,
    merge: [],
    hidden: {},
    position: {},
    accentID: null,
    sceneID: null,
  }
}

export function countSeededPrints(draft: GalleryPatternDraft): number {
  return Object.keys(draft.print).length
}
